import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { extractError, useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Pencil, Trash2, FilePlus2, Download } from "lucide-react";

function ConfirmedEffect({ show }) {
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-white/70 backdrop-blur-sm pointer-events-none animate-[fadeOut_1.2s_ease-in-out_forwards]">
      <div className="flex flex-col items-center gap-3">
        <svg width="72" height="72" viewBox="0 0 72 72">
          <circle
            cx="36" cy="36" r="32"
            fill="none" stroke="#059669" strokeWidth="4"
            strokeDasharray="201" strokeDashoffset="201"
            className="animate-[circleDraw_0.5s_ease-out_forwards]"
          />
          <path
            d="M22 37 L32 47 L50 27"
            fill="none" stroke="#059669" strokeWidth="4"
            strokeLinecap="round" strokeLinejoin="round"
            strokeDasharray="40" strokeDashoffset="40"
            className="animate-[checkDraw_0.3s_ease-out_0.4s_forwards]"
          />
        </svg>
        <span className="text-emerald-700 font-medium text-sm tracking-wide">Booking confirmed</span>
      </div>
    </div>
  );
}

export default function BookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bk, setBk] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [activity, setActivity] = useState([]);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelNotes, setCancelNotes] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [showConfirmedEffect, setShowConfirmedEffect] = useState(false);
  const formatDate = (d) => {
    if (!d) return "";
    const date = new Date(d);
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };
   
  const formatTime = (t) =>
    t ? new Date(`1970-01-01T${t}`).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }) : "";
  const renderCustomValue = (v) => {
    if (v == null || v === "") return "—";
    if (typeof v === "object" && v.file_id) {
      const isImage = (v.content_type || "").startsWith("image/");
      return (
        <div className="space-y-1">
          {isImage && (
            <img src={`${BACKEND_URL}/api/files/${v.file_id}`} alt={v.filename} className="max-h-40 rounded border border-black/10" />
          )}
          <a href={`${BACKEND_URL}/api/files/${v.file_id}`} target="_blank" rel="noreferrer" className="text-[#D85C38] hover:underline text-xs" data-testid={`booking-file-${v.file_id}`}>
            {v.filename || "Download file"}
          </a>
        </div>
      );
    }
    if (typeof v === "boolean") return v ? "Yes" : "No";
    return String(v);
  };

  const load = async () => {
    const [b, invs, act] = await Promise.all([
      api.get(`/bookings/${id}`),
      api.get(`/invoices`, { params: { booking_id: id } }),
      api.get(`/activity-log`, { params: { booking_id: id } }),
    ]);
    setBk(b.data);
    setInvoices(invs.data.items || invs.data || []);
    setActivity(act.data.items || act.data || []);
  };

  useEffect(() => { load(); }, [id]);

  const setStatus = async (status) => {
    try {
      await api.post(`/bookings/${id}/status`, { status });
      toast.success("Status updated");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const cancel = async () => {
    try {
      await api.post(`/bookings/${id}/status`, { status: "cancelled", cancellation_reason: cancelReason, cancellation_notes: cancelNotes });
      setCancelOpen(false);
      toast.success("Booking cancelled");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const removeBooking = async () => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Booking deleted");
      navigate("/app/bookings");
    } catch (e) { toast.error(extractError(e)); }
  };

  const downloadPdf = async () => {
    try {
      setExportingPdf(true);
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      const node = document.getElementById("booking-pdf-root");
      if (!node) throw new Error("Booking export container not found");
      const canvas = await html2canvas(node, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 10;
      pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight - 20;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight + 10;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight - 20;
      }
      pdf.save(`${bk.client_name || "booking"}.pdf`);
      toast.success("Booking PDF downloaded");
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setExportingPdf(false);
    }
  };

  if (!bk) return <div className="text-sm text-zinc-500">Loading…</div>;

  const canEdit = user.role === "admin" || user.role === "manager";

  return (
    <div className="space-y-6" data-testid="booking-detail-page">
      <ConfirmedEffect show={showConfirmedEffect} />
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">{bk.event_type_name}</div>
          <h1 className="font-serif text-4xl tracking-tighter mt-1 text-[#1A1A1A]">{bk.client_name}</h1>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          {canEdit && bk.status !== "cancelled" && bk.status !== "completed" ? (
            <Select
              value={bk.status}
              onValueChange={(value) => {
                if (value === "cancelled") {
                  setCancelOpen(true);
                } else {
                  setStatus(value);
                  if (value === "confirmed") {
                    setShowConfirmedEffect(true);
                    setTimeout(() => setShowConfirmedEffect(false), 1200);
                  }
                }
              }}
            >
              <SelectTrigger className="w-30 h-9 bg-white capitalize" data-testid="booking-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled" className="text-red-600">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <Badge variant="outline" className="capitalize h-9 px-3 flex items-center" data-testid="booking-detail-status">
              {bk.status}
            </Badge>
          )}

          {canEdit && (
            <Link to={`/app/bookings/new?edit_id=${bk.id}`}>
              <Button variant="outline" className="h-9">
                <Pencil size={14} className="mr-1.5" /> Edit
              </Button>
            </Link>
          )}

          {canEdit && (
            <Button
              variant="outline"
              className="h-9 border-red-200 text-red-700 hover:bg-red-50"
              onClick={removeBooking}
              data-testid="booking-delete-btn"
            >
              <Trash2 size={14} className="mr-1.5" /> Delete
            </Button>
          )}

          <Button variant="outline" className="h-9" onClick={downloadPdf} disabled={exportingPdf} data-testid="booking-download-pdf-btn">
            <Download size={14} className="mr-1.5" /> {exportingPdf ? "Preparing…" : "Download PDF"}
          </Button>

          {canEdit && bk.status !== "cancelled" && (
            <Link to={`/app/invoices/new?booking_id=${bk.id}`} className="ml-auto">
              <Button className="bg-[#1A1A1A] text-white hover:bg-black rounded-full h-9 px-5" data-testid="booking-create-invoice-btn">
                <FilePlus2 size={14} className="mr-1.5" /> Create invoice
              </Button>
            </Link>
          )}
        </div>

        {/* Cancel dialog — unchanged, just now triggered from the status select */}
        <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Cancel this booking?</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Select value={cancelReason} onValueChange={setCancelReason}>
                <SelectTrigger data-testid="cancel-reason-select"><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Client request">Client request</SelectItem>
                  <SelectItem value="Venue changed">Venue changed</SelectItem>
                  <SelectItem value="Scheduling conflict">Scheduling conflict</SelectItem>
                  <SelectItem value="Client emergency">Client emergency</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              <Textarea
                placeholder="Refund / notes (optional)"
                value={cancelNotes}
                onChange={(e) => setCancelNotes(e.target.value)}
                data-testid="cancel-notes-input"
              />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setCancelOpen(false)}>Back</Button>
              <Button onClick={cancel} className="bg-red-600 hover:bg-red-700 text-white" data-testid="cancel-confirm-btn">
                Cancel booking
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>

      <div id="booking-pdf-root" className="fixed left-[-9999px] top-0 w-[794px] bg-white p-8 text-sm" style={{ fontFamily: "Inter, Arial, sans-serif" }}>
        <div className="font-serif text-3xl tracking-tighter text-[#1A1A1A]">{bk.client_name}</div>
        <div className="mt-2 text-zinc-600">{bk.event_type_name}</div>
        <div className="mt-6 border-t border-black/10 pt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Date</div>
              <div className="mt-1">{bk.end_date && bk.end_date !== bk.event_date ? `${formatDate(bk.event_date)} → ${formatDate(bk.end_date)}` : formatDate(bk.event_date)}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Location</div>
              <div className="mt-1">{bk.location || "—"}</div>
            </div>
          </div>
          <div className="mt-6">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Client</div>
            <div className="mt-1">{bk.client_name}</div>
            <div className="text-zinc-600">{bk.client_phone}</div>
            {bk.client_email && <div className="text-zinc-600">{bk.client_email}</div>}
          </div>
          {Object.keys(bk.custom_data || {}).length > 0 && (
            <div className="mt-6">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Custom details</div>
              <div className="mt-2 space-y-2">
                {Object.entries(bk.custom_data || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3 border-b border-black/5 pb-2">
                    <span className="text-zinc-600">{k}</span>
                    <span className="text-right">{renderCustomValue(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {bk.special_instructions && (
            <div className="mt-6">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Special instructions</div>
              <div className="mt-1 whitespace-pre-wrap">{bk.special_instructions}</div>
            </div>
          )}
          {bk.client_note && (
            <div className="mt-6">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Note</div>
              <div className="mt-1 whitespace-pre-wrap">{bk.client_note}</div>
            </div>
          )}
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            <Card className="lg:col-span-2 bg-white border-black/10">
              <CardHeader><CardTitle className="text-sm uppercase tracking-[0.2em] text-zinc-500 font-medium">Booking Details</CardTitle></CardHeader>
              <CardContent>
                  <div className="mt-2">
                    <dt className="text-xs uppercase tracking-[0.18em] text-zinc-500">Event Type</dt>
                    <dd className="mt-1 text-sm whitespace-pre-wrap">{bk.event_type_name}</dd>
                  </div>
                  <div className="mt-6">
                    <dt className="text-xs uppercase tracking-[0.18em] text-zinc-500">Date</dt>
                    <dd className="mt-1 text-sm whitespace-pre-wrap">
                    {bk.end_date && bk.end_date !== bk.event_date
                      ? `${formatDate(bk.event_date)} → ${formatDate(bk.end_date)}`
                      : formatDate(bk.event_date)}
                    {bk.event_start_time && ` · ${formatTime(bk.event_start_time)}`}
                    {bk.event_end_time && `–${formatTime(bk.event_end_time)}`} 
                    </dd>
                  </div>
                  <div className="mt-6">
                    <dt className="text-xs uppercase tracking-[0.18em] text-zinc-500">Location</dt>
                    <dd className="mt-1 text-sm whitespace-pre-wrap">
                    {bk.location && `${bk.location}`}
                    </dd>
                  </div>
                  <hr className="border-t-2 border-[#D85C38] my-6" /> 
                {Object.keys(bk.custom_data || {}).length === 0 ? (
                  <div className="text-sm text-zinc-500">No custom fields filled.</div>
                ) : (
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(bk.custom_data).map(([k, v]) => (
                      <div key={k}>
                        <dt className="text-xs uppercase tracking-[0.18em] text-zinc-500">{k}</dt>
                        <dd className="mt-1 text-sm">{renderCustomValue(v)}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                {bk.special_instructions && (
                  <div className="mt-6">
                    <dt className="text-xs uppercase tracking-[0.18em] text-zinc-500">Special instructions</dt>
                    <dd className="mt-1 text-sm whitespace-pre-wrap">{bk.special_instructions}</dd>
                  </div>
                )}
                {bk.client_note && (
                  <div className="mt-6">
                    <dt className="text-xs uppercase tracking-[0.18em] text-zinc-500">Note</dt>
                    <dd className="mt-1 text-sm whitespace-pre-wrap">{bk.client_note}</dd>
                  </div>
                )}
              </CardContent>
            </Card>
            <Card className="bg-white border-black/10">
              <CardHeader><CardTitle className="text-sm uppercase tracking-[0.2em] text-zinc-500 font-medium">Client & team</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div>
                  <div className="text-zinc-500 text-xs uppercase tracking-[0.18em]">Client</div>
                  <div className="font-medium">{bk.client_name}</div>
                  <div className="text-zinc-600">{bk.client_phone}</div>
                  {bk.client_email && <div className="text-zinc-600">{bk.client_email}</div>}
                </div>
                <div>
                  <div className="text-zinc-500 text-xs uppercase tracking-[0.18em]">Assigned</div>
                  <div>{(bk.assigned_employee_names || []).join(", ") || "—"}</div>
                </div>
                {bk.cancellation_reason && (
                  <div>
                    <div className="text-zinc-500 text-xs uppercase tracking-[0.18em]">Cancellation</div>
                    <div className="font-medium">{bk.cancellation_reason}</div>
                    {bk.cancellation_notes && <div className="text-zinc-600 text-xs">{bk.cancellation_notes}</div>}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="invoices" className="mt-6">
          <Card className="bg-white border-black/10">
            <CardContent className="p-0">
              {invoices.length === 0 ? (
                <div className="p-8 text-center text-sm text-zinc-500">No invoices yet for this booking.</div>
              ) : (
                <ul className="divide-y divide-black/5">
                  {invoices.map((inv) => (
                    <li key={inv.id} className="p-4 flex items-center justify-between" data-testid={`booking-invoice-${inv.id}`}>
                      <div>
                        <Link to={`/app/invoices/${inv.id}`} className="font-medium hover:underline">{inv.invoice_number}</Link>
                        <div className="text-xs text-zinc-500 capitalize">{inv.status.replace("_", " ")}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">₹{(inv.total || 0).toLocaleString()}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="activity" className="mt-6">
          <Card className="bg-white border-black/10">
            <CardContent className="p-0">
              <ul className="divide-y divide-black/5">
                {activity.length === 0 && <li className="p-6 text-sm text-zinc-500">No activity logged yet.</li>}
                {activity.map((a) => (
                  <li key={a.id} className="p-4">
                    <div className="text-sm">{a.details}</div>
                    <div className="text-xs text-zinc-500">{a.user_name} · {new Date(a.created_at).toLocaleString()}</div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
